import { useEffect, useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { highlightSearchedValue } from '@rossum/components/filtering/highlightSearchedValue';
import SearchInput from '@rossum/components/search-input/SearchInput';
import { IconChevronDown, IconChevronUp } from '@rossum/ui/icons/tabler';
import {
  Checkbox,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Radio,
  Skeleton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { filterWorkspaces } from '../helpers/filterWorkspaces';

type Workspaces = Array<{
  id: number;
  name: string;
  queues: Array<{ id: number; name: string }>;
}>;

type QueueMultiselectListProps = {
  topSelections?: number[];
  onSelect: (queues: number[]) => void;
  selectedQueues: number[];
  searchInputProps?: { dataCy?: string };
  queuesByWorkspace: Workspaces;
  isLoading?: boolean;
  multiSelect?: boolean;
  dense?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
};

export const QueueSelectList = ({
  topSelections = [],
  searchInputProps,
  onSelect,
  selectedQueues,
  queuesByWorkspace,
  isLoading,
  multiSelect = true,
  dense,
  disabled,
  autoFocus,
}: QueueMultiselectListProps) => {
  const intl = useIntl();
  const [search, setSearch] = useState<string>('');
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<number[]>([]);

  const isSearchActive = search.length > 1;

  const workspaces = useMemo<Workspaces>(() => {
    if (!queuesByWorkspace) return [];

    const toNormalize = isSearchActive
      ? filterWorkspaces(search, queuesByWorkspace)
      : queuesByWorkspace;

    return toNormalize
      .filter(({ queues }) => !!queues.length)
      .map(({ name, queues, id }) => ({
        id,
        name,
        queues: queues.map(queue => ({
          id: queue.id,
          name: queue.name,
        })),
      }));
  }, [isSearchActive, queuesByWorkspace, search]);

  const selectedQueuesSet = useMemo(
    () => new Set(selectedQueues),
    [selectedQueues]
  );

  // this is so that we don't have to do nested iterations O(n²) to handle selectedQueues
  const sortedWorkspaces = useMemo(() => {
    const normalizedWorkspaces = workspaces.map(workspace => {
      const isFullySelected = !!workspace?.queues.every(({ id }) =>
        selectedQueuesSet.has(id)
      );
      const isPartiallySelected = !!workspace?.queues.some(({ id }) =>
        selectedQueuesSet.has(id)
      );
      return {
        ...workspace,
        isFullySelected,
        isPartiallySelected,
      };
    });

    return R.partition(normalizedWorkspaces, option =>
      option.queues.some(({ id }) => topSelections.includes(id))
    ).flat();
  }, [workspaces, selectedQueuesSet, topSelections]);

  // Collapse all workspaces when user clears the search.
  // Because searching expands all matching workspaces which clutters the UI
  useEffect(() => {
    if (!isSearchActive) setExpandedWorkspaces([]);
  }, [isSearchActive]);

  // Expand workspaces whose queues match the active search.
  useEffect(() => {
    if (!isSearchActive) return;

    const workspacesToExpand = workspaces.map(workspace => workspace.id);

    if (workspacesToExpand.length) {
      setExpandedWorkspaces(prev => [
        ...new Set([...prev, ...workspacesToExpand]),
      ]);
    }
  }, [isSearchActive, workspaces]);

  const handleSelect = (ids: number[]) => {
    onSelect(ids);
  };

  const hasWokspaces = sortedWorkspaces.length > 0;
  return (
    <Stack sx={{ height: '100%' }}>
      <SearchInput
        disabled={disabled}
        onChange={setSearch}
        value={search}
        withStartAdornment={false}
        autoFocus={autoFocus}
        sx={{ mb: 2, width: '100%' }}
        slotProps={
          searchInputProps?.dataCy
            ? {
                htmlInput: { 'data-cy': searchInputProps.dataCy },
              }
            : undefined
        }
      />
      {isLoading ? (
        <Stack width="100%">
          {[1, 2, 3, 4, 5].map(number => (
            <Stack key={number} flexDirection="row" alignItems="center" gap={2}>
              <Checkbox disabled sx={{ p: 0 }} />
              <Skeleton height={dense ? 30 : 56} width="100%" variant="text" />
              <SvgIcon fontSize="medium">
                <IconChevronDown />
              </SvgIcon>
            </Stack>
          ))}
        </Stack>
      ) : hasWokspaces ? (
        <Stack spacing={1} width="100%" sx={{ overflow: 'auto' }}>
          <List disablePadding={dense} dense={dense}>
            {sortedWorkspaces.map(workspace => {
              return (
                <Stack key={workspace.id}>
                  <ListItemButton
                    disableGutters={dense}
                    onClick={() =>
                      setExpandedWorkspaces(prev =>
                        prev.includes(workspace.id)
                          ? prev.filter(id => id !== workspace.id)
                          : [...prev, workspace.id]
                      )
                    }
                    sx={dense ? { p: 0, pl: 1 } : {}}
                    disabled={disabled}
                  >
                    {multiSelect ? (
                      <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                        <Checkbox
                          data-cy="queue-list-workspace-checkbox"
                          disabled={disabled}
                          indeterminate={
                            !workspace.isFullySelected &&
                            workspace.isPartiallySelected
                          }
                          checked={workspace.isFullySelected}
                          onClickCapture={e => {
                            e.stopPropagation();
                            const queuesOfThisWorkspace = workspace.queues.map(
                              ({ id }) => id
                            );
                            const updatedQueues = workspace.isFullySelected
                              ? selectedQueues.filter(
                                  id => !queuesOfThisWorkspace.includes(id)
                                )
                              : [...selectedQueues, ...queuesOfThisWorkspace];
                            handleSelect(updatedQueues);
                          }}
                          sx={{ p: 0 }}
                        />
                      </ListItemIcon>
                    ) : null}
                    <Typography
                      mr="auto"
                      variant="body2"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {isSearchActive
                        ? highlightSearchedValue(workspace.name, search)
                        : workspace.name}
                    </Typography>
                    <SvgIcon fontSize="medium">
                      {expandedWorkspaces.includes(workspace.id) ? (
                        <IconChevronUp />
                      ) : (
                        <IconChevronDown />
                      )}
                    </SvgIcon>
                  </ListItemButton>
                  <Collapse in={expandedWorkspaces.includes(workspace.id)}>
                    <List disablePadding dense sx={{ pb: 1 }}>
                      {workspace.queues.map(queue => {
                        const doesAlreadyExist = selectedQueuesSet.has(
                          queue.id
                        );
                        return (
                          <ListItem
                            disablePadding
                            disableGutters
                            key={queue.id}
                            dense
                            sx={{ pl: 2 }}
                          >
                            <ListItemButton
                              disabled={disabled}
                              disableRipple
                              onClick={() => {
                                if (!multiSelect) {
                                  handleSelect(
                                    selectedQueues.includes(queue.id)
                                      ? []
                                      : [queue.id]
                                  );
                                } else {
                                  const updatedQueues = doesAlreadyExist
                                    ? selectedQueues.filter(
                                        id => id !== queue.id
                                      )
                                    : selectedQueues.concat(queue.id);

                                  handleSelect(updatedQueues);
                                }
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>
                                {multiSelect ? (
                                  <Checkbox
                                    disabled={disabled}
                                    checked={doesAlreadyExist}
                                    sx={{ p: 0 }}
                                  />
                                ) : (
                                  <Radio
                                    disabled={disabled}
                                    checked={doesAlreadyExist}
                                    sx={{ p: 0 }}
                                  />
                                )}
                              </ListItemIcon>
                              <Tooltip title={queue.name}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {isSearchActive
                                    ? highlightSearchedValue(queue.name, search)
                                    : queue.name}
                                </Typography>
                              </Tooltip>
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                </Stack>
              );
            })}
          </List>
        </Stack>
      ) : (
        <Stack sx={{ justifyContent: 'center', alignItems: 'center', p: 2 }}>
          <Typography color="text.secondary">
            {intl.formatMessage({
              id: 'components.queueSelectList.noQueuesFound',
            })}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};
